/**
 * Card admin para mostrar el préstamo Aplazo y sus reembolsos asociados.
 *
 * Endpoints consumidos (los crea el agente backend):
 *  - GET  /api/admin/pedidos/:id/aplazo            → loan + refunds
 *  - POST /api/admin/pedidos/:id/aplazo/sync       → sincroniza estado con Aplazo API
 *  - POST /api/admin/pedidos/:id/aplazo            → { action: 'refund' | 'cancel', amount? }
 *
 * Estados Aplazo:
 *  - PENDING    → aún sin aprobar (recién creado, en evaluación)
 *  - ACTIVO     → préstamo activo
 *  - CANCELADO  → cliente o admin canceló antes de activar
 *  - DEVUELTO   → reembolsado total
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CreditCard,
  RotateCw,
  XCircle,
  Loader2,
  AlertCircle,
  Copy,
  CheckCheck,
  RefreshCw,
} from 'lucide-react'
import { Badge } from './Badge'
import { apiGet, apiPost, formatMXN } from '../lib/api'

interface AplazoRefund {
  id: string
  refundId: string | null
  amount: number
  status: string
  reason: string | null
  requestedBy: string | null
  createdAt: string
}

interface AplazoLoanDetail {
  loan: {
    id: string
    loanId: string
    cartId: string
    status: string
    amount: number
    checkoutUrl: string | null
    environment: string
    webhookReceivedAt: string | null
    settlementDate: string | null
    createdAt: string
    updatedAt: string
  } | null
  refunds: AplazoRefund[]
}

function loanStatusBadge(status: string) {
  const s = (status || '').toUpperCase()
  if (s === 'ACTIVO' || s === 'ACTIVE')
    return <Badge variant="success">Activo</Badge>
  if (s === 'CANCELADO' || s === 'CANCELLED' || s === 'CANCELED')
    return <Badge variant="danger">Cancelado</Badge>
  if (s === 'DEVUELTO' || s === 'REFUNDED' || s === 'RETURNED')
    return <Badge variant="danger">Devuelto</Badge>
  if (s === 'PENDING' || s === 'PENDIENTE')
    return <Badge variant="warning">Pendiente</Badge>
  return <Badge>{status || 'Sin estado'}</Badge>
}

function refundStatusBadge(status: string) {
  const s = (status || '').toUpperCase()
  if (s === 'APPROVED' || s === 'APROBADO' || s === 'COMPLETED' || s === 'COMPLETADO')
    return <Badge variant="success">Aprobado</Badge>
  if (s === 'REJECTED' || s === 'RECHAZADO' || s === 'FAILED')
    return <Badge variant="danger">Rechazado</Badge>
  if (s === 'PENDING' || s === 'PENDIENTE')
    return <Badge variant="warning">Pendiente</Badge>
  return <Badge>{status || '—'}</Badge>
}

function formatDateTime(iso: string | null) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d)
  } catch {
    return iso
  }
}

function truncate(s: string, len = 18): string {
  if (!s) return ''
  if (s.length <= len) return s
  return `${s.slice(0, len - 3)}…`
}

function CopyableId({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  if (!value) return <span className="text-ink-500">—</span>
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-xs">
      <span title={value}>{truncate(value, 18)}</span>
      <button
        type="button"
        onClick={() => {
          try {
            navigator.clipboard.writeText(value)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          } catch {
            /* noop */
          }
        }}
        className="rounded p-0.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
        title="Copiar"
      >
        {copied ? <CheckCheck className="size-3 text-emerald-600" /> : <Copy className="size-3" />}
      </button>
    </span>
  )
}

export function AplazoCard({ orderId }: { orderId: string }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [refundAmount, setRefundAmount] = useState<string>('')
  const [refundReason, setRefundReason] = useState<string>('')

  const { data, isLoading, isError } = useQuery<{ data: AplazoLoanDetail }>({
    queryKey: ['pedido-aplazo', orderId],
    queryFn: () => apiGet(`/api/admin/pedidos/${orderId}/aplazo`),
  })

  function flash(msg: string) {
    setSuccess(msg)
    setError(null)
    setTimeout(() => setSuccess(null), 2500)
  }

  function fail(e: unknown) {
    const msg = e instanceof Error ? e.message : 'Error de red'
    setError(msg)
    setSuccess(null)
  }

  const syncMut = useMutation({
    mutationFn: () => apiPost(`/api/admin/pedidos/${orderId}/aplazo/sync`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pedido-aplazo', orderId] })
      queryClient.invalidateQueries({ queryKey: ['pedido', orderId] })
      flash('Estado sincronizado con Aplazo')
    },
    onError: fail,
  })

  const refundMut = useMutation({
    mutationFn: (payload: { amount?: number; reason: string; full: boolean }) =>
      apiPost(`/api/admin/pedidos/${orderId}/aplazo`, {
        action: 'refund',
        amount: payload.full ? undefined : payload.amount,
        reason: payload.reason || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pedido-aplazo', orderId] })
      queryClient.invalidateQueries({ queryKey: ['pedido', orderId] })
      setRefundAmount('')
      setRefundReason('')
      flash('Reembolso solicitado')
    },
    onError: fail,
  })

  const cancelMut = useMutation({
    mutationFn: () =>
      apiPost(`/api/admin/pedidos/${orderId}/aplazo`, { action: 'cancel' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pedido-aplazo', orderId] })
      queryClient.invalidateQueries({ queryKey: ['pedido', orderId] })
      flash('Préstamo cancelado')
    },
    onError: fail,
  })

  if (isLoading) {
    return (
      <section className="rounded-2xl border border-ink-200 bg-white">
        <header className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-ink-900">
            <CreditCard className="size-4 text-teal-600" />
            Aplazo
          </div>
        </header>
        <div className="flex items-center gap-2 p-5 text-xs text-ink-500">
          <Loader2 className="size-3.5 animate-spin" /> Cargando…
        </div>
      </section>
    )
  }

  if (isError) {
    return (
      <section className="rounded-2xl border border-ink-200 bg-white">
        <header className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-ink-900">
            <CreditCard className="size-4 text-teal-600" />
            Aplazo
          </div>
        </header>
        <div className="p-5 text-sm text-red-600">
          No se pudo cargar la información del préstamo.
        </div>
      </section>
    )
  }

  const loan = data?.data.loan
  const refunds = data?.data.refunds ?? []

  if (!loan) {
    return (
      <section className="rounded-2xl border border-ink-200 bg-white">
        <header className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-ink-900">
            <CreditCard className="size-4 text-teal-600" />
            Aplazo
          </div>
        </header>
        <div className="p-5 text-sm text-ink-500">
          Este pedido se pagó con Aplazo pero aún no se ha creado el préstamo en BD. Pulsa
          "Sincronizar" para intentar recuperar el estado.
          <div className="mt-3">
            <button
              type="button"
              onClick={() => syncMut.mutate()}
              disabled={syncMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50"
            >
              {syncMut.isPending ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <RefreshCw className="size-3" />
              )}
              Sincronizar
            </button>
          </div>
        </div>
      </section>
    )
  }

  const upperStatus = loan.status.toUpperCase()
  const canCancel = upperStatus === 'PENDING' || upperStatus === 'PENDIENTE' || upperStatus === 'ACTIVO' || upperStatus === 'ACTIVE'
  const canRefund = upperStatus === 'ACTIVO' || upperStatus === 'ACTIVE'
  const totalRefunded = refunds
    .filter((r) => {
      const s = r.status.toUpperCase()
      return s === 'APPROVED' || s === 'COMPLETED' || s === 'APROBADO' || s === 'COMPLETADO' || s === 'PENDING' || s === 'PENDIENTE'
    })
    .reduce((sum, r) => sum + Number(r.amount || 0), 0)
  const remaining = Math.max(0, Number(loan.amount) - totalRefunded)

  return (
    <section className="rounded-2xl border border-ink-200 bg-white">
      <header className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink-900">
          <CreditCard className="size-4 text-teal-600" />
          Aplazo
          <span className="ml-1">{loanStatusBadge(loan.status)}</span>
          {loan.environment === 'sandbox' && (
            <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">
              Pruebas
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => syncMut.mutate()}
          disabled={syncMut.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50"
        >
          {syncMut.isPending ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <RotateCw className="size-3" />
          )}
          Sincronizar estado
        </button>
      </header>

      <div className="p-5">
        {error && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertCircle className="size-3.5" /> {error}
          </div>
        )}
        {success && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            <CheckCheck className="size-3.5" /> {success}
          </div>
        )}

        {/* Datos del préstamo */}
        <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-ink-500">Loan ID</div>
            <div className="mt-0.5">
              <CopyableId value={loan.loanId} />
            </div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-ink-500">Cart ID</div>
            <div className="mt-0.5">
              <CopyableId value={loan.cartId} />
            </div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-ink-500">Monto</div>
            <div className="mt-0.5 font-semibold text-ink-900">{formatMXN(loan.amount)}</div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-ink-500">Saldo pendiente</div>
            <div className="mt-0.5 font-semibold text-ink-900">{formatMXN(remaining)}</div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-ink-500">Creado</div>
            <div className="mt-0.5 text-ink-700">{formatDateTime(loan.createdAt)}</div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-ink-500">Webhook recibido</div>
            <div className="mt-0.5 text-ink-700">{formatDateTime(loan.webhookReceivedAt)}</div>
          </div>
        </div>

        {loan.checkoutUrl && upperStatus === 'PENDING' && (
          <a
            href={loan.checkoutUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-teal-700 hover:underline"
          >
            Abrir checkout de Aplazo →
          </a>
        )}

        {/* Reembolsos */}
        <div className="mt-5 border-t border-ink-100 pt-4">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-ink-900">Reembolsos</h4>
            <span className="text-xs text-ink-500">{refunds.length} registrados</span>
          </div>

          {refunds.length === 0 ? (
            <p className="text-xs text-ink-500">Aún no se han solicitado reembolsos para este préstamo.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-ink-50 text-left text-[10px] uppercase tracking-wider text-ink-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Refund ID</th>
                    <th className="px-3 py-2 font-medium">Monto</th>
                    <th className="px-3 py-2 font-medium">Estado</th>
                    <th className="px-3 py-2 font-medium">Motivo</th>
                    <th className="px-3 py-2 font-medium">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {refunds.map((r) => (
                    <tr key={r.id} className="border-t border-ink-100">
                      <td className="px-3 py-2">
                        <CopyableId value={r.refundId || ''} />
                      </td>
                      <td className="px-3 py-2 font-semibold text-ink-900">{formatMXN(r.amount)}</td>
                      <td className="px-3 py-2">{refundStatusBadge(r.status)}</td>
                      <td className="px-3 py-2 text-ink-700">{r.reason || '—'}</td>
                      <td className="px-3 py-2 text-ink-500">{formatDateTime(r.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Form reembolso parcial */}
          {canRefund && (
            <div className="mt-4 rounded-lg border border-ink-200 bg-ink-50 p-3">
              <div className="text-xs font-semibold text-ink-700">Solicitar reembolso parcial</div>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <div className="flex-1">
                  <label className="block">
                    <span className="mb-1 block text-[10px] uppercase tracking-wide text-ink-500">
                      Monto (MXN)
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={remaining}
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      placeholder={`Máximo ${formatMXN(remaining)}`}
                      className="w-full rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-brand-500"
                    />
                  </label>
                </div>
                <div className="flex-1">
                  <label className="block">
                    <span className="mb-1 block text-[10px] uppercase tracking-wide text-ink-500">
                      Motivo (opcional)
                    </span>
                    <input
                      type="text"
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      maxLength={200}
                      placeholder="ej: cliente devolvió 1 producto"
                      className="w-full rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-brand-500"
                    />
                  </label>
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => {
                      const amt = Number(refundAmount)
                      if (!Number.isFinite(amt) || amt <= 0) {
                        setError('Ingresa un monto válido')
                        return
                      }
                      if (amt > remaining + 0.01) {
                        setError(`El monto supera el saldo (${formatMXN(remaining)})`)
                        return
                      }
                      refundMut.mutate({ amount: amt, reason: refundReason.trim(), full: false })
                    }}
                    disabled={refundMut.isPending}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                  >
                    {refundMut.isPending && <Loader2 className="size-3.5 animate-spin" />}
                    Reembolso parcial
                  </button>
                </div>
              </div>

              {/* Reembolso total */}
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-ink-200 pt-3">
                <span className="text-xs text-ink-600">
                  Reembolsar el saldo completo restante:{' '}
                  <strong className="text-ink-900">{formatMXN(remaining)}</strong>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(
                        `¿Reembolsar el saldo completo de ${formatMXN(remaining)} al cliente? Esta acción no se puede deshacer.`,
                      )
                    ) {
                      refundMut.mutate({
                        reason: refundReason.trim() || 'Reembolso total',
                        full: true,
                      })
                    }
                  }}
                  disabled={refundMut.isPending || remaining <= 0}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  Reembolso TOTAL
                </button>
              </div>
            </div>
          )}

          {/* Cancelación del préstamo */}
          {canCancel && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="text-xs text-amber-900">
                <strong className="block">Cancelar préstamo</strong>
                <span>Disponible solo mientras el préstamo está pendiente o recién activo.</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (
                    window.confirm(
                      '¿Cancelar el préstamo en Aplazo? Esto detiene los cobros futuros al cliente.',
                    )
                  ) {
                    cancelMut.mutate()
                  }
                }}
                disabled={cancelMut.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                {cancelMut.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <XCircle className="size-3.5" />
                )}
                Cancelar préstamo
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
